import {
  DxCard,
  DxCardContent,
  DxCardHeader,
  DxList,
  DxContainer,
} from '@dvag/design-system-react';
import { featureFlags } from '@dvag/dfs-constant-config';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Item } from 'component/BottomCards/Card/Item';
import { route } from 'config/route';
import { useGetPolicies } from 'hooks/useGetPolicies';
import { buildPath } from 'service/buildPath';
import { checkIcon } from 'utils/util';

import {
  BALANCE_SHEET,
  COVERAGE_GAPS,
  COVERAGE_GAPS_FEATURE_FLAG,
  COVERAGE_GAPS_POLICIES,
  leftCardSections,
  LeftCardSectionData,
} from './pages.config';

const coverageGapsChildPolicies = new Set<string>([
  COVERAGE_GAPS_POLICIES.workforce,
  COVERAGE_GAPS_POLICIES.pension,
]);

const getSubSectionsByPolicy = (
  section: LeftCardSectionData,
  policies: string[],
  useNewPermissionsApi: boolean,
): LeftCardSectionData[] => {
  const subSections = section.subSections ?? [];

  const hasCoverageGapsChildPolicy = policies.some((policy) =>
    coverageGapsChildPolicies.has(policy),
  );

  if (section.id === COVERAGE_GAPS && (!useNewPermissionsApi || !hasCoverageGapsChildPolicy)) {
    return subSections;
  }

  if (!policies.length) {
    return subSections;
  }

  return subSections.filter(({ policy }) => !policy || policies.includes(policy));
};

const canShowSection = (
  section: LeftCardSectionData,
  policies: string[],
  showBalanceSheet: boolean,
  isCoverageGapsFeatureEnabled: boolean,
  useNewPermissionsApi: boolean,
) => {
  if (section.id === BALANCE_SHEET && !showBalanceSheet) {
    return false;
  }

  if (section.id === COVERAGE_GAPS && !isCoverageGapsFeatureEnabled) {
    return false;
  }

  if (section.id === COVERAGE_GAPS) {
    return true;
  }

  if (section.policy && !policies.includes(section.policy)) {
    return false;
  }

  return (
    !section.subSections ||
    getSubSectionsByPolicy(section, policies, useNewPermissionsApi).length > 0
  );
};

export const LeftCard = ({ showBalanceSheet = false }: { showBalanceSheet?: boolean }) => {
  const { householdId, section } = useParams();
  const navigate = useNavigate();
  const isCoverageGapsFeatureEnabled = useFeatureIsOn(COVERAGE_GAPS_FEATURE_FLAG);
  const useNewPermissionsApi = useFeatureIsOn(featureFlags.canUsePermissionsApi);
  const { data: policyData } = useGetPolicies({
    enabled: isCoverageGapsFeatureEnabled && useNewPermissionsApi,
  });

  const policies = policyData?.policies ?? [];

  const rootSections = leftCardSections.filter((cardSection) =>
    canShowSection(
      cardSection,
      policies,
      showBalanceSheet,
      isCoverageGapsFeatureEnabled,
      useNewPermissionsApi,
    ),
  );
  const coverageGapsSection = rootSections.find(({ id }) => id === COVERAGE_GAPS);
  const isCoverageGapsSection = section === COVERAGE_GAPS && !!coverageGapsSection;
  const currentSections = isCoverageGapsSection
    ? getSubSectionsByPolicy(coverageGapsSection, policies, useNewPermissionsApi)
    : rootSections;
  const cardTitle = isCoverageGapsSection ? coverageGapsSection.title : 'Allfinanzinhalte';

  return (
    <DxCard data-testid="left-card">
      {isCoverageGapsSection ? (
        <DxCardHeader
          label={cardTitle}
          icon={checkIcon('pfeil-links')}
          iconcolor="default"
          onClick={() =>
            navigate(buildPath(route.advisoryDashboard, { householdId: householdId as string }))
          }
          style={{ cursor: 'pointer' }}
          data-testid="coverage-gaps-title"
        />
      ) : (
        <DxCardHeader label={cardTitle} />
      )}
      <DxCardContent className="dashboard-card-content">
        <DxContainer className="flex column">
          <DxList size="m">
            {currentSections.map(({ icon, id, title, navigate: navigateSection, contentIcon }) => (
              <Item
                id={id}
                key={id}
                title={title}
                icon={icon}
                onItemClick={() => {
                  if (id === COVERAGE_GAPS) {
                    navigate(
                      buildPath(route.advisoryDashboardIdSection, {
                        householdId: householdId as string,
                        section: COVERAGE_GAPS,
                      }),
                    );
                    return;
                  }

                  navigateSection(householdId as string);
                }}
                contentIcon={contentIcon}
              />
            ))}
          </DxList>
        </DxContainer>
      </DxCardContent>
    </DxCard>
  );
};
